import {
  Paper,
  Title,
  Text,
  Badge,
  Group,
  Stack,
  Grid,
  Box,
  Image,
  ThemeIcon,
  Divider,
  LoadingOverlay,
} from "@mantine/core";

import {
  IconEye,
  IconBrain,
  IconEyeglass,
  IconCircleCheck,
  IconAlertTriangle,
  IconBuilding,
  IconUser,
  IconPhone,
  IconMapPin,
  IconClipboardList,
} from "@tabler/icons-react";

import { PatientHeader } from "./components/PatientHeader";

import { useResultsSummaryData, type ResultsSummaryTestIds } from "./hooks/useResultsSummaryData";

import { Printable } from "@components";

interface TestSummary {
  id: number;
  title: string;
  icon: React.ReactNode;
  status: "normal" | "attention" | "critical";
  statusLabel: string;
  statusColor: string;
  score: string;
  description: string;
  recommendation: string;
}

interface Hospital {
  name: string;
  address: string;
  phone: string;
  department: string;
}

interface Doctor {
  name: string;
  specialty: string;
  hospital: string;
  phone: string;
  logo?: string;
  logoWidth?: number;
}

export interface ResultsSummaryProps {
  testIds?: ResultsSummaryTestIds;
}

export default function ResultsSummary({ testIds }: ResultsSummaryProps = {}) {
  const { patientData, loading } = useResultsSummaryData(testIds);

  const testSummaries: TestSummary[] = [
    {
      id: 1,
      title: "Vision Screening",
      icon: <IconEye size={24} color="white" />,
      status: "normal",
      statusLabel: "Excellent",
      statusColor: "green",
      score: "Excellent",
      description:
        "All vision tests including Visual Field, Visual Acuity, Contrast Sensitivity, and Color Vision are within normal ranges.",
      recommendation: "Continue annual eye examinations to monitor visual development.",
    },
    {
      id: 2,
      title: "Neurological Assessment",
      icon: <IconBrain size={24} color="white" />,
      status: "normal",
      statusLabel: "Excellent",
      statusColor: "green",
      score: "Excellent",
      description:
        "Pupillary Responses, Ocular Tracking, and Vestibular Function are all within normal age-appropriate ranges. No signs of neurological impairment.",
      recommendation: "Continue monitoring during regular check-ups.",
    },
    {
      id: 3,
      title: "Lazy Eye (Amblyopia) Screening",
      icon: <IconEyeglass size={24} color="white" />,
      status: "normal",
      statusLabel: "Excellent",
      statusColor: "green",
      score: "Excellent",
      description:
        "Eye alignment and visual acuity are balanced between both eyes. No signs of amblyopia or strabismus detected.",
      recommendation: "Continue with annual eye examinations to ensure healthy visual development.",
    },
  ];

  const recommendedHospitals: Hospital[] = [
    {
      name: "Moorfields Eye Hospital Dubai",
      address: "Al Razi Building 64, Dubai Healthcare City, Dubai, UAE",
      phone: "+971 4 429 7888",
      department: "Ophthalmology",
    },
    {
      name: "Cleveland Clinic Abu Dhabi",
      address: "Al Maryah Island, Abu Dhabi, UAE",
      phone: "+971 2 501 9000",
      department: "Eye Institute",
    },
    {
      name: "Mediclinic City Hospital",
      address: "Building 37, Dubai Healthcare City, Dubai, UAE",
      phone: "+971 4 435 9999",
      department: "Ophthalmology Center",
    },
  ];

  const recommendedDoctors: Doctor[] = [
    {
      name: "Dr. Fatima Al Hashimi, MD",
      specialty: "Ophthalmology & Strabismus",
      hospital: "Moorfields Eye Hospital Dubai",
      phone: "+971 4 429 7889",
      logo: "/img/hospitals/moorfields-logo.png",
      logoWidth: 140,
    },
    {
      name: "Dr. Ahmed Al Mansouri, MD",
      specialty: "Ophthalmology",
      hospital: "Cleveland Clinic Abu Dhabi",
      phone: "+971 2 501 9100",
      logo: "/img/hospitals/cleveland-logo.png",
      logoWidth: 90,
    },
    {
      name: "Dr. Sarah Al Maktoum, MD",
      specialty: "Eye Diseases & Amblyopia",
      hospital: "Mediclinic City Hospital",
      phone: "+971 4 435 9900",
      logo: "/img/hospitals/mediclinic-logo.webp",
      logoWidth: 120,
    },
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "normal":
        return <IconCircleCheck size={20} />;
      case "attention":
        return <IconAlertTriangle size={20} />;
      default:
        return <IconCircleCheck size={20} />;
    }
  };

  return (
    <Printable.Section>
      <Box>
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }} />
        <PatientHeader
          patientData={patientData}
          icon={<IconClipboardList size={24} color="white" />}
          title="Comprehensive Results Summary"
          subtitle="Complete Eye & Neurological Screening Report"
        />

        {/* Test Results Overview */}
        <Paper shadow="md" p="xl" radius="md" mb="md" withBorder>
          <Title order={3} mb="lg">
            Test Results Overview
          </Title>

          <Stack>
            {testSummaries.map((test) => (
              <Paper
                key={test.id}
                p="lg"
                radius="md"
                withBorder
                style={{
                  backgroundColor:
                    test.status === "attention"
                      ? "var(--oculera-tint-yellow)"
                      : "var(--oculera-tint-green)",
                  borderColor:
                    test.status === "attention"
                      ? "var(--oculera-tint-yellow-border)"
                      : "var(--oculera-tint-green-border)",
                }}
              >
                <Group justify="space-between" align="flex-start">
                  <Group gap="md" align="flex-start" style={{ flex: 1 }}>
                    <ThemeIcon size={48} radius="md" color={test.statusColor} variant="filled">
                      {test.icon}
                    </ThemeIcon>
                    <Box style={{ flex: 1 }}>
                      <Title order={4} mb="xs">
                        {test.title}
                      </Title>
                      <Text size="sm" c="dimmed" mb="sm">
                        {test.description}
                      </Text>
                      <Text size="sm" fw={500}>
                        <Text span fw={600}>
                          Recommendation:{" "}
                        </Text>
                        {test.recommendation}
                      </Text>
                    </Box>
                  </Group>
                  <Badge
                    color={test.statusColor}
                    variant="filled"
                    size="lg"
                    leftSection={getStatusIcon(test.status)}
                    style={{ flexShrink: 0 }}
                  >
                    {test.statusLabel}
                  </Badge>
                </Group>
              </Paper>
            ))}
          </Stack>
        </Paper>

        {/* Recommended Providers for Regular Check-ups */}
        <Paper
          shadow="md"
          p="xl"
          radius="md"
          mb="md"
          withBorder
          style={{
            backgroundColor: "var(--oculera-tint-green)",
            borderColor: "var(--oculera-tint-green-border)",
          }}
        >
          <Group gap="sm" mb="lg">
            <ThemeIcon size={40} radius="md" color="green" variant="filled">
              <IconCircleCheck size={24} />
            </ThemeIcon>
            <Box>
              <Title order={3} c="green.9">
                Recommended Providers for Regular Check-ups
              </Title>
              <Text size="sm" c="green.8">
                Schedule your annual eye examination with one of our trusted partners
              </Text>
            </Box>
          </Group>

          <Text size="sm" c="green.9" mb="lg">
            Regular eye examinations are important to maintain healthy vision and catch any
            potential issues early. We recommend scheduling annual check-ups with a qualified
            ophthalmologist to ensure continued healthy visual development.
          </Text>

          <Divider my="lg" color="green.3" />

          {/* Recommended Hospitals */}
          <Title order={4} mb="md" c="green.9">
            <Group gap="xs">
              <IconBuilding size={20} />
              Recommended Hospitals
            </Group>
          </Title>

          <Grid gutter="md" mb="xl">
            {recommendedHospitals.map((hospital, index) => (
              <Grid.Col key={index} span={4}>
                <Paper
                  p="md"
                  radius="md"
                  withBorder
                  style={{
                    height: "100%",
                    backgroundColor: "var(--oculera-surface)",
                    borderColor: "var(--oculera-tint-green-border)",
                  }}
                >
                  <Text fw={600} size="sm" mb="xs" c="inherit">
                    {hospital.name}
                  </Text>
                  <Badge size="sm" color="green" variant="light" mb="sm">
                    {hospital.department}
                  </Badge>
                  <Group gap="xs" mb="xs">
                    <IconMapPin size={14} color="var(--mantine-color-gray-6)" />
                    <Text size="xs" c="dimmed">
                      {hospital.address}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <IconPhone size={14} color="var(--mantine-color-gray-6)" />
                    <Text size="xs" c="green.6" fw={500}>
                      {hospital.phone}
                    </Text>
                  </Group>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>

          {/* Recommended Doctors */}
          <Title order={4} mb="md" c="green.9">
            <Group gap="xs">
              <IconUser size={20} />
              Recommended Specialists
            </Group>
          </Title>

          <Grid gutter="md">
            {recommendedDoctors.map((doctor, index) => (
              <Grid.Col key={index} span={4}>
                <Paper
                  p="md"
                  radius="md"
                  shadow="md"
                  withBorder
                  style={{
                    height: "100%",
                    backgroundColor: "var(--oculera-surface)",
                    borderColor: "var(--oculera-tint-green-border)",
                  }}
                >
                  <Group justify="space-between" align="flex-start">
                    <Box style={{ flex: 1 }}>
                      <Text fw={600} size="sm" mb="xs" c="inherit">
                        {doctor.name}
                      </Text>
                      <Badge size="sm" color="teal" variant="light" mb="sm">
                        {doctor.specialty}
                      </Badge>
                      <Group gap="xs" mb="xs">
                        <IconBuilding size={14} color="var(--mantine-color-gray-6)" />
                        <Text size="xs" c="dimmed">
                          {doctor.hospital}
                        </Text>
                      </Group>
                      <Group gap="xs">
                        <IconPhone size={14} color="var(--mantine-color-gray-6)" />
                        <Text size="xs" c="green.6" fw={500}>
                          {doctor.phone}
                        </Text>
                      </Group>
                    </Box>
                    {doctor.logo && (
                      <Image
                        src={doctor.logo}
                        alt={`${doctor.hospital} logo`}
                        w={doctor.logoWidth}
                        h="auto"
                        fit="contain"
                        style={{ flexShrink: 0 }}
                      />
                    )}
                  </Group>
                </Paper>
              </Grid.Col>
            ))}
          </Grid>
        </Paper>

        {/* Summary Footer */}
        <Paper
          shadow="md"
          p="xl"
          radius="md"
          withBorder
          style={{
            backgroundColor: "var(--oculera-tint-green)",
            borderColor: "var(--oculera-tint-green-border)",
          }}
        >
          <Title order={4} mb="md" c="green.8">
            Summary & Next Steps
          </Title>
          <Text size="sm" c="green.9" mb="md">
            {patientData.name} has completed comprehensive vision and neurological screening. All
            test results are within normal ranges and show excellent visual and neurological
            development.
          </Text>
          <Text size="sm" c="green.9" fw={600}>
            Continue with annual eye and neurological check-ups to monitor healthy development. No
            immediate action is required at this time.
          </Text>
        </Paper>

        <Text size="xs" c="dimmed" ta="center" mt="md" mb="md">
          This report is for informational purposes. Please consult with your ophthalmologist or
          neurologist for medical advice.
        </Text>
      </Box>
    </Printable.Section>
  );
}
